<template>
  <div id="payment-modal" class="pd" v-if="paymentId !== ''">
    <div v-if="paymentFull">
      <div id="print-area" ref="printArea">
        <!-- Encabezado: monto y estados -->
        <div class="pd-header" :class="{ 'pd-header--refund': isRefund }">
          <div>
            <div class="pd-header__eyebrow">
              {{ isRefund ? "POS refund" : "POS payment" }} #{{
                paymentFull.id
              }}
            </div>
            <div
              class="pd-header__amount"
              :class="{ 'pd-header__amount--refund': isRefund }"
            >
              {{ isRefund ? "−" : "" }}{{ money(Math.abs(totalCharged)) }}
            </div>
            <div v-if="paymentFull.paymentFee > 0" class="pd-header__sub">
              Order {{ money(orderAmountWithoutFee) }} + fee
              {{ money(paymentFull.paymentFee) }}
            </div>
            <div
              v-else-if="paymentFull.orderDescription"
              class="pd-header__sub"
            >
              {{ paymentFull.orderDescription }}
            </div>
          </div>
          <div class="pd-header__side">
            <div class="pd-pills">
              <span v-if="isRefund" class="pd-pill pd-pill--red">↩ REFUND</span>
              <span v-else class="pd-pill pd-pill--green">✓ APPROVED</span>
              <span v-if="refundStatus" class="pd-pill pd-pill--yellow">{{
                refundStatus
              }}</span>
              <span
                v-if="paymentFull.infoautoProcessed == 1"
                class="pd-pill pd-pill--green"
                >Infoauto: procesado</span
              >
              <span v-else class="pd-pill pd-pill--yellow"
                >Infoauto: sin procesar</span
              >
              <span v-if="paymentFull.ccName" class="pd-pill pd-pill--stripe">{{
                paymentFull.ccName
              }}</span>
            </div>
            <div class="pd-header__date">{{ paymentFull.date }}</div>
          </div>
        </div>

        <PaymentRefundBanner
          :is-refund="isRefund"
          :amount="paymentFull.orderAmount"
          :original-id="paymentFull.refundOf ? paymentFull.refundOf.id : null"
          :original-reference="
            paymentFull.refundOf ? paymentFull.refundOf.transactionId : null
          "
          :by="isRefund ? paymentFull.userCreator : null"
          :date="paymentFull.date"
          :refunded-amount="Number(paymentFull.amountRefunded || 0)"
          :refunds="
            (paymentFull.refunds || []).map(r => ({
              id: r.id,
              amount: r.orderAmount
            }))
          "
          @openPayment="$emit('openPayment', $event)"
        />

        <div class="pd-grid">
          <PaymentDetailSection title="Amounts">
            <PaymentDetailField
              :label="isRefund ? 'Amount refunded' : 'Order amount'"
            >
              <span :class="{ 'pd-refund-amount': isRefund }">{{
                money(orderAmountWithoutFee)
              }}</span>
            </PaymentDetailField>
            <PaymentDetailField
              v-if="paymentFull.paymentFee > 0"
              label="Payment fee"
            >
              <span class="pd-fee">+{{ money(paymentFull.paymentFee) }}</span>
            </PaymentDetailField>
            <PaymentDetailField
              v-if="paymentFull.paymentFee > 0"
              label="Total charged"
              strong
              :value="money(paymentFull.orderAmount)"
            />
            <PaymentDetailField
              label="Shipping"
              :value="optionalMoney(paymentFull.shippingAmount)"
            />
            <PaymentDetailField
              label="Tax"
              :value="optionalMoney(paymentFull.taxAmount)"
            />
            <StripeLiveRefundStatus
              v-if="isStripePayment"
              :key="`live-${paymentFull.id}-${paymentFull.amountRefunded}`"
              :payment-intent-id="paymentFull.transactionId"
              :pos-transaction-id="paymentFull.id"
            />
          </PaymentDetailSection>

          <PaymentDetailSection title="Customer & sale">
            <PaymentDetailField
              label="Customer"
              :value="paymentFull.customerName"
            />
            <PaymentDetailField
              label="Email"
              :value="paymentFull.customerEmail"
            />
            <PaymentDetailField label="Order number">
              <router-link
                v-if="paymentFull.orderNumber"
                :to="orderRoute(paymentFull.orderNumber)"
                target="_blank"
                class="pd-link"
                >{{ paymentFull.orderNumber }}</router-link
              >
              <span v-else class="text-gray-300">—</span>
            </PaymentDetailField>
            <PaymentDetailField
              label="Description"
              :value="paymentFull.orderDescription"
            />
            <PaymentDetailField
              label="Source"
              :value="paymentFull.orderSource"
            />
            <PaymentDetailField
              label="Seller"
              :value="paymentFull.sellerName"
            />
            <PaymentDetailField
              label="Processed by"
              :value="paymentFull.userCreator"
            />
          </PaymentDetailSection>

          <PaymentDetailSection title="Payment method">
            <PaymentDetailField
              label="Processor / card name"
              :value="paymentFull.ccName"
            />
            <PaymentDetailField
              label="Card last digits"
              :value="paymentFull.ccLastNum"
              mono
            />
            <PaymentDetailField label="Zip code" :value="paymentFull.ccZip" />
          </PaymentDetailSection>

          <PaymentDetailSection title="Transaction">
            <PaymentDetailField label="POS ID" :value="paymentFull.id" mono />
            <PaymentDetailField
              label="Transaction ID"
              :value="paymentFull.transactionId"
              mono
            />
            <PaymentDetailField
              label="Transaction tag"
              :value="paymentFull.transactionTag"
              mono
            />
            <PaymentDetailField label="Response">
              <details
                v-if="isLongText(paymentFull.transactionResponse)"
                class="pd-raw"
              >
                <summary>View response</summary>
                <pre>{{ prettyText(paymentFull.transactionResponse) }}</pre>
              </details>
              <template v-else-if="paymentFull.transactionResponse">{{
                paymentFull.transactionResponse
              }}</template>
              <span v-else class="text-gray-300">—</span>
            </PaymentDetailField>
          </PaymentDetailSection>
        </div>

        <!-- Órdenes que cubre el cobro -->
        <div
          v-if="paymentFull.orders && paymentFull.orders.length > 0"
          class="pd-table-card"
        >
          <div class="pd-table-card__title">
            <span>Orders</span>
            <span>{{ paymentFull.orders.length }}</span>
          </div>
          <div class="pd-table-wrap">
            <table class="pd-table">
              <thead>
                <tr>
                  <th>Order number</th>
                  <th class="text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="order in paymentFull.orders" :key="order.id">
                  <td>
                    <router-link
                      :to="orderRoute(order.orderNumber)"
                      target="_blank"
                      class="pd-link"
                      >{{ order.orderNumber }}</router-link
                    >
                  </td>
                  <td
                    class="text-right"
                    :class="{ 'pd-refund-amount': Number(order.amount) < 0 }"
                  >
                    {{ money(order.amount) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Reembolsos del cobro -->
        <div
          v-if="paymentFull.refunds && paymentFull.refunds.length > 0"
          class="pd-table-card"
        >
          <div class="pd-table-card__title">
            <span>↩ Refunds</span>
            <span>{{ paymentFull.refunds.length }}</span>
          </div>
          <div class="pd-table-wrap">
            <table class="pd-table">
              <thead>
                <tr>
                  <th>POS #</th>
                  <th>Date</th>
                  <th>Stripe refund</th>
                  <th>Infoauto</th>
                  <th class="text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="refund in paymentFull.refunds" :key="refund.id">
                  <td>
                    <button
                      class="pd-link"
                      @click="$emit('openPayment', String(refund.id))"
                    >
                      #{{ refund.id }}
                    </button>
                  </td>
                  <td>{{ refund.date }}</td>
                  <td class="pd-mono">{{ refund.transactionId }}</td>
                  <td>
                    <span
                      class="pd-pill"
                      :class="
                        refund.infoautoProcessed == 1
                          ? 'pd-pill--green'
                          : 'pd-pill--yellow'
                      "
                      >{{
                        refund.infoautoProcessed == 1
                          ? "Procesado"
                          : "Sin procesar"
                      }}</span
                    >
                  </td>
                  <td class="text-right pd-refund-amount">
                    −{{ money(Math.abs(refund.orderAmount)) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="pd-actions no-print">
        <button
          v-if="paymentFull.infoautoProcessed != 1"
          class="pd-btn pd-btn--yellow"
          :disabled="markingProcessed"
          @click="markAsProcessed()"
        >
          {{ markingProcessed ? "Procesando..." : "Marcar como procesado" }}
        </button>
        <button class="pd-btn" @click="print()">🖨 Print</button>
        <button class="pd-btn pd-btn--blue" @click="openEmailModal()">
          ✉ Send receipt by email
        </button>
        <button
          v-if="isStripePayment && canRefund"
          class="pd-btn pd-btn--stripe"
          @click="showStripeModal = true"
        >
          Stripe details / Refund
        </button>
      </div>

      <StripePaymentDetailModal
        v-if="showStripeModal && canRefund"
        :show="showStripeModal"
        :pos-transaction-id="paymentFull.id"
        :payment-intent-id="paymentFull.transactionId"
        @close="showStripeModal = false"
        @refunded="onRefunded"
      />

      <SendReceiptModal
        v-if="showEmailModal"
        :default-email="paymentFull.customerEmail || ''"
        :is-refund="isRefund"
        :send-fn="email => sendPosReceipt(paymentFull.id, email)"
        @close="showEmailModal = false"
      />
    </div>
    <div v-else class="pd-loading">
      <BaseSmallSpinner />
    </div>
  </div>
</template>
<script>
import {
  getPosPayment as getPaymentApi,
  sendPosReceipt,
  markInfoautoProcessed
} from "@/api/store/PaymentsService.js";
import StripePaymentDetailModal from "@/components/order/StripePaymentDetailModal.vue";
import StripeLiveRefundStatus from "@/components/payments/StripeLiveRefundStatus.vue";
import PaymentRefundBanner from "@/components/payments/PaymentRefundBanner.vue";
import PaymentDetailSection from "@/components/payments/PaymentDetailSection.vue";
import PaymentDetailField from "@/components/payments/PaymentDetailField.vue";
import paymentDetailHelpers from "@/components/payments/paymentDetailHelpers.js";
import SendReceiptModal from "@/components/payments/SendReceiptModal.vue";
import printElement from "@/utils/print-element.js";
export default {
  name: "PaymentModal",
  components: {
    StripePaymentDetailModal,
    StripeLiveRefundStatus,
    PaymentRefundBanner,
    PaymentDetailSection,
    PaymentDetailField,
    SendReceiptModal
  },
  mixins: [paymentDetailHelpers],
  props: {
    paymentId: {
      type: String,
      required: true
    },
    value: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      paymentFull: null,
      isOpenModal: this.value,
      loading: true,
      showEmailModal: false,
      markingProcessed: false,
      showStripeModal: false
    };
  },
  mounted() {
    this.getPayment();
    window.addEventListener("keypress", this.closeKey);
  },
  computed: {
    isOpen() {
      return this.value;
    },
    totalCharged() {
      return Number(this.paymentFull?.orderAmount || 0);
    },
    // order_amount incluye el fee; el monto de la orden es sin él
    orderAmountWithoutFee() {
      return (
        Number(this.paymentFull?.orderAmount || 0) -
        Number(this.paymentFull?.paymentFee || 0)
      );
    },
    // Estado de reembolso según amount_refunded del pago original
    refundStatus() {
      const refunded = Number(this.paymentFull?.amountRefunded || 0);
      if (refunded <= 0) return null;
      return refunded >= Number(this.paymentFull.orderAmount) - 0.005
        ? "REFUNDED"
        : "PARTIALLY REFUNDED";
    },
    isRefund() {
      return (
        !!this.paymentFull &&
        (Number(this.paymentFull.orderAmount) < 0 ||
          !!this.paymentFull.refundOfId)
      );
    },
    // Cobro Stripe original (las filas de refund tienen transaction_id re_...)
    isStripePayment() {
      return (
        String(this.paymentFull?.transactionId || "").startsWith("pi_") &&
        Number(this.paymentFull?.orderAmount) > 0 &&
        !this.paymentFull?.refundOfId
      );
    },
    canRefund() {
      return this.$store.getters["auth/userHasPermission"](
        "can_refund_payment"
      );
    }
  },
  watch: {
    isOpenModal(val) {
      this.$emit("input", val);
      if (!val) {
        this.$emit("closedPayment");
      }
    },
    value(val) {
      this.isOpenModal = val;
    },
    paymentId(val) {
      if (val != "") {
        this.paymentFull = null;
        this.getPayment();
      }
    }
  },
  methods: {
    closeKey(event) {
      // No cerrar mientras se escribe (p. ej. la nota del refund) ni con el
      // modal de Stripe abierto.
      const target = event.target;
      if (
        this.showStripeModal ||
        this.showEmailModal ||
        (target &&
          (["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName) ||
            target.isContentEditable))
      ) {
        return;
      }
      if (event.keyCode === 113) {
        this.$emit("closedPayment");
      }
    },
    close() {
      this.$emit("closedPayment");
    },
    async onRefunded() {
      // Refresca la lista de refunds del cobro y la tabla de pagos POS
      this.paymentFull = await getPaymentApi(this.paymentId);
      this.$emit("transactionUpdated");
    },
    async getPayment() {
      if (this.paymentId == "") {
        return;
      }
      this.paymentFull = await getPaymentApi(this.paymentId);
      this.loading = false;
    },
    print() {
      printElement(this.$refs.printArea, {
        title: `POS ${this.isRefund ? "refund" : "payment"} #${
          this.paymentFull.id
        }`
      });
    },
    openEmailModal() {
      this.showEmailModal = true;
    },
    sendPosReceipt(paymentId, email) {
      return sendPosReceipt(paymentId, email);
    },
    async markAsProcessed() {
      this.markingProcessed = true;
      try {
        await markInfoautoProcessed(this.paymentFull.id);
        this.paymentFull.infoautoProcessed = 1;
        this.$emit("transactionUpdated");
      } catch {
        this.$toast.open({
          message: "Error marking as processed",
          type: "error",
          position: "top-right"
        });
      } finally {
        this.markingProcessed = false;
      }
    }
  },
  beforeDestroy() {
    window.removeEventListener("keypress", this.closeKey);
  }
};
</script>
<style lang="scss" scoped>
@import "@/assets/style/css/payment-detail.scss";
</style>
